import React, { useState, useEffect, useMemo } from "react";
import { ChevronUp, Search, RefreshCw, ArrowUp } from "lucide-react";
import Logo from "../assets/mainlogo.png";
import ReactMarkdown from "react-markdown";
const Menu = () => {
  const [activeCategory, setActiveCategory] = useState(null);
  const [extraDataExpanded, setExtraDataExpanded] = useState(false);
  const [extraData, setExtraData] = useState([]);
  const [menuData, setMenuData] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [impress, setImpress] = useState(false);

  useEffect(() => {
    document.title = "Baccaratlounge | Menu";

    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const fetchData = async () => {
    setError(null);

    try {
      const response = await fetch("https://admin.baccaratlounge.de/api/menucard", {
        headers: {
          Authorization: "Bearer cpgEkc8bYCPgD39rmLQZ2568",
        },
      });

      if (!response.ok) throw new Error("Netzwerkfehler");

      const data = await response.json();
      setMenuData(data);

      const responseExtra = await fetch("https://admin.baccaratlounge.de/api/tabaksorten/get", {
        method: "POST",
        headers: {
          Authorization: "Bearer cpgEkc8bYCPgD39rmLQZ2568"
        }
      })

      if (!responseExtra.ok) throw new Error("Netzwerkfehler");

      const data2 = await responseExtra.json()
      setExtraData(data2)

    } catch (err) {
      setError(
        "Fehler beim Laden der Menükarte. Bitte versuchen Sie es später erneut. " + err
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const groupedItems = useMemo(() => {
    return menuData.reduce((acc, category) => {
      acc[category.displayName] = {
        products: (category.products || [])
          .map((product) => ({
            name: product.displayName,
            description: product.description,
            quantity: product.quantity,
            quantityUnit: product.quantityUnit,
            price: product.price,
            discountPrice: product.discountPrice,
            status: product.status,
          }))
          .sort((a, b) => a.price - b.price),

        subgroups: (category.subgroups || []).map((subgroup) => ({
          name: subgroup.displayName,
          products: (subgroup.products || [])
            .map((product) => ({
              name: product.displayName,
              description: product.description,
              quantity: product.quantity,
              quantityUnit: product.quantityUnit,
              price: product.price,
              discountPrice: product.discountPrice,
              status: product.status,
            }))
            .sort((a, b) => a.price - b.price),
        })),
      };
      return acc;
    }, {});
  }, [menuData]);

  const filteredItems = useMemo(() => {
    if (!searchTerm) return groupedItems;

    const searchTermLower = searchTerm.toLowerCase();
    return Object.keys(groupedItems).reduce((acc, category) => {
      const categoryData = groupedItems[category];

      // Filter main products
      const filteredProducts = categoryData.products.filter(
        (product) =>
          product.name.toLowerCase().includes(searchTermLower) ||
          product.description?.toLowerCase().includes(searchTermLower)
      );

      // Filter subgroups and their products
      const filteredSubgroups = categoryData.subgroups
        .map((subgroup) => ({
          ...subgroup,
          products: subgroup.products.filter(
            (product) =>
              product.name.toLowerCase().includes(searchTermLower) ||
              product.description?.toLowerCase().includes(searchTermLower)
          ),
        }))
        .filter((subgroup) => subgroup.products.length > 0);

      // Only include category if it has matching items
      if (filteredProducts.length > 0 || filteredSubgroups.length > 0) {
        acc[category] = {
          products: filteredProducts,
          subgroups: filteredSubgroups,
        };
      }

      return acc;
    }, {});
  }, [groupedItems, searchTerm]);

  const filteredCategories = useMemo(
    () => Object.keys(filteredItems),
    [filteredItems]
  );

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-r from-[#1B1919] to-[#1B1919] text-white flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 animate-spin mx-auto mb-4" />
          <p className="text-xl">Lade Menükarte...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-r from-[#1B1919] to-[#1B1919] text-white flex items-center justify-center p-4">
        <div className="bg-red-900/50 border border-red-500 rounded-lg p-6 max-w-md text-center">
          <p className="mb-4">{error}</p>
          <button
            onClick={fetchData}
            className="inline-flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-r from-black to-[#1B1919] text-white">
      {/* Header Section */}
      <div className="flex flex-col items-center py-10">
        <div className="mb-8 p-4">
          <img src={Logo} alt="Baccarat Lounge Logo" className="opacity-100 drop-shadow-[0_0_10px_rgba(255,223,186,0.3)]" />
        </div>
        <p className="text-lg text-center text-amber-200/80 font-light tracking-wider">
          Genieße die besten Shishas und Drinks
        </p>
      </div>

      {/* Search Bar */}
      <div className="max-w-4xl mx-auto px-4 mb-8">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-amber-200/60 pointer-events-none" />
          <input
            type="text"
            placeholder="Menü durchsuchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-black/50 border border-amber-200/20 rounded-lg text-amber-100 placeholder-amber-200/40 focus:outline-none focus:ring-2 focus:ring-amber-200/50 focus:border-amber-200/50 transition-all shadow-[0_0_15px_rgba(255,223,186,0.1)]"
          />
        </div>
      </div>

      {/* Extra Data Section (Tabaksorten) */}
      {extraData && extraData.length > 0 && extraData[0].markdown && (
        <div className="max-w-4xl mx-auto px-4 mb-10">
          <button
            onClick={() => setExtraDataExpanded(!extraDataExpanded)}
            className="w-full cursor-pointer bg-black/50 p-4 rounded-lg shadow-lg hover:bg-black/70 transition-all focus:outline-none focus:ring-2 focus:ring-amber-200/50 border border-amber-200/20 shadow-[0_0_15px_rgba(255,223,186,0.1)]"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-3xl font-bold text-amber-200 drop-shadow-[0_0_5px_rgba(255,223,186,0.3)]">Tabaksorten</h2>
              <ChevronUp
                className={`transform transition-transform duration-200 text-amber-200/80 ${extraDataExpanded ? "rotate-0" : "rotate-180"
                  }`}
              />
            </div>
          </button>

          {extraDataExpanded && (
            <div className="mt-4 bg-black/50 border border-amber-200/20 p-6 rounded-lg shadow-[0_0_15px_rgba(255,223,186,0.1)]">
              <div className="prose prose-img:rounded-xl prose-headings:text-amber-200 prose-p:text-amber-100/90 prose-li:text-amber-100/90 prose-a:text-amber-200 prose-strong:text-amber-200">
                <ReactMarkdown>{extraData[0].markdown}</ReactMarkdown>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Menu Content */}
      <div className="max-w-4xl mx-auto px-4 pb-20">
        {filteredCategories.length === 0 && !(extraData && extraData.length > 0 && extraData[0].markdown && searchTerm) ? (
          <>
          </>
        ) : (
          filteredCategories.map((category) => (
            <div key={category} className="mb-6">
              <button
                onClick={() =>
                  setActiveCategory(activeCategory === category ? null : category)
                }
                className="w-full cursor-pointer bg-black/50 p-4 rounded-lg shadow-lg hover:bg-black/70 transition-all focus:outline-none focus:ring-2 focus:ring-amber-200/50 border border-amber-200/20 shadow-[0_0_15px_rgba(255,223,186,0.1)]"
              >
                <div className="flex items-center justify-between">
                  <h2 className="text-3xl font-bold text-amber-200 drop-shadow-[0_0_5px_rgba(255,223,186,0.3)]">{category}</h2>
                  <ChevronUp
                    className={`transform transition-transform duration-200 text-amber-200/80 ${activeCategory === category ? "rotate-0" : "rotate-180"
                      }`}
                  />
                </div>
              </button>

              {activeCategory === category && (
                <div className="mt-4 space-y-6">
                  {filteredItems[category].products.map((item, index) => (
                    <div
                      key={index}
                      className="bg-black/50 p-6 rounded-lg shadow-lg hover:scale-105 transition-all border border-amber-200/20 shadow-[0_0_15px_rgba(255,223,186,0.1)]"
                    >
                      {item.status !== 2 ? (
                        <h3 className="text-xl font-semibold text-amber-200">{item.name}</h3>
                      ) : (
                        <div>
                          <h3 className="text-xl font-semibold line-through text-amber-200/50">
                            {item.name}
                          </h3>
                          <p className="font-bold text-red-400">AUSVERKAUFT</p>
                        </div>
                      )}

                      {item.description && (
                        <p className="mt-2 text-amber-100/70 whitespace-normal break-words">
                          {item.description}
                        </p>
                      )}

                      <p className="text-amber-100/70 mt-4">
                        {item.quantity === 0 ? null : item.quantity + ' ' + item.quantityUnit}
                      </p>

                      <span className="block mt-3 text-2xl font-bold text-amber-200 drop-shadow-[0_0_5px_rgba(255,223,186,0.3)]">
                        {item.discountPrice
                          ? `${item.discountPrice.toFixed(2)}€ (statt ${item.price.toFixed(2)}€)`
                          : `${item.price.toFixed(2)}€`}
                      </span>
                    </div>
                  ))}

                  {filteredItems[category].subgroups.map(
                    (subgroup, subIndex) => (
                      <div
                        key={subIndex}
                        className="mt-10 bg-[#3A3A3A] p-6 rounded-lg shadow-lg"
                      >
                        <h3 className="text-2xl font-bold border-b-2 border-gray-600 pb-2">
                          {subgroup.name}
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mt-4">
                          {subgroup.products.map((item, idx) => (
                            <div
                              key={idx}
                              className="bg-[#2F2F2F] p-4 rounded-lg hover:scale-105 transition-transform duration-200"
                            >
                              <h4 className="text-xl font-semibold">
                                {item.name}
                              </h4>
                              {item.description && (
                                <p className="text-gray-400">
                                  {item.description}
                                </p>
                              )}
                              <p className="text-gray-400">
                                {item.quantity === 0 ? null : item.quantity + ' ' + item.quantityUnit}
                              </p>
                              <span className="block mt-2 text-lg font-bold">
                                {item.price.toFixed(2)}€
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <div className="flex flex-row items-center px-4 py-8 justify-center">
        <p className="cursor-pointer text-amber-200/80 hover:text-amber-200 transition-colors underline"
          onClick={() => setImpress(true)}>
          Impressum
        </p>
      </div>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-black/50 p-3 rounded-full shadow-lg hover:bg-black/70 transition-all focus:outline-none focus:ring-2 focus:ring-amber-200/50 border border-amber-200/20 shadow-[0_0_15px_rgba(255,223,186,0.1)]"
          aria-label="Nach oben scrollen"
        >
          <ArrowUp className="w-6 h-6 text-amber-200" />
        </button>
      )}

      {impress === true ? (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-black/50 max-w-2xl mx-auto rounded-lg shadow-xl border border-amber-200/20 shadow-[0_0_15px_rgba(255,223,186,0.1)] w-full">
            <div className="p-4 border-b border-amber-200/20 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-amber-200 drop-shadow-[0_0_5px_rgba(255,223,186,0.3)]">Impressum</h2>
              <button
                onClick={() => setImpress(false)}
                className="bg-black/50 hover:bg-black/70 text-amber-200 rounded-full p-2 transition-all focus:outline-none focus:ring-2 focus:ring-amber-200/50 border border-amber-200/20"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

            <div className="overflow-y-auto p-6 max-h-[70vh]">
              <div className="space-y-6 text-left">
                <div className="bg-black/50 p-4 rounded-lg border border-amber-200/20 shadow-[0_0_15px_rgba(255,223,186,0.1)]">
                  <h3 className="text-xl font-semibold text-amber-200 mb-2 drop-shadow-[0_0_5px_rgba(255,223,186,0.3)]">Edmond Brecalia</h3>
                  <p className="text-amber-100/70">
                    Baccarat Lounge<br />
                    Bahnhofstraße 81<br />
                    63165 Mühlheim am Main
                  </p>
                </div>

                <div className="bg-black/50 p-4 rounded-lg border border-amber-200/20 shadow-[0_0_15px_rgba(255,223,186,0.1)]">
                  <h3 className="text-xl font-semibold text-amber-200 mb-2 drop-shadow-[0_0_5px_rgba(255,223,186,0.3)]">Kontakt</h3>
                  <p className="text-amber-100/70">
                    Telefon: <a href="tel:+491737585585" className="text-amber-200 hover:text-amber-300 transition-colors">+49 173 7585585</a><br />
                    E-Mail: <a href="mailto:brecalia@gmx.de" className="text-amber-200 hover:text-amber-300 transition-colors">brecalia@gmx.de</a>
                  </p>
                </div>

                <div className="bg-black/50 p-4 rounded-lg border border-amber-200/20 shadow-[0_0_15px_rgba(255,223,186,0.1)]">
                  <h3 className="text-xl font-semibold text-amber-200 mb-2 drop-shadow-[0_0_5px_rgba(255,223,186,0.3)]">Umsatzsteuer-ID</h3>
                  <p className="text-amber-100/70">
                    Umsatzsteuer-Identifikationsnummer gemäß § 27a Umsatzsteuergesetz:<br />
                    <span className="font-medium text-amber-200">Folgt in Kürze</span>
                  </p>
                </div>

                <div className="bg-black/50 p-4 rounded-lg border border-amber-200/20 shadow-[0_0_15px_rgba(255,223,186,0.1)]">
                  <h3 className="text-xl font-semibold text-amber-200 mb-2 drop-shadow-[0_0_5px_rgba(255,223,186,0.3)]">Gewerbeanmeldung</h3>
                  <p className="text-amber-100/70">
                    Die Gewerbeerlaubnis nach § 14 GewO wurde am 19.12.2024 von folgender Stelle erteilt: Magistrat der Stadt Mühlheim am Main.
                  </p>
                </div>

                <div className="bg-black/50 p-4 rounded-lg border border-amber-200/20 shadow-[0_0_15px_rgba(255,223,186,0.1)]">
                  <h3 className="text-xl font-semibold text-amber-200 mb-2 drop-shadow-[0_0_5px_rgba(255,223,186,0.3)] break-words hyphens-auto">
                    Verbraucherstreitbeilegung
                  </h3>
                  <p className="text-amber-100/70">
                    Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.
                  </p>
                </div>
              </div>

              <div className="mt-6 text-center text-sm">
                <p className="text-amber-100/50">Quelle: <a href="https://www.e-recht24.de" target="_blank" rel="noopener noreferrer" className="text-amber-200/70 hover:text-amber-200 transition-colors">eRecht24</a></p>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Menu;